import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/format';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { toast } from 'sonner';
import { Eye, RotateCcw, Ban } from 'lucide-react';

/*
  Page history (Super Admin): the undo entries recorded for one page (a project, the inventory catalog …), with
  Details, Restore, Cancel and per-step Undo. Colours come from the status tokens (StatusPill domain "history"),
  so the dialog reads the same in the light and dark themes.
*/

const CARD = 'rounded-card border border-line bg-surface-2';
const ROW = 'rounded-lg border border-line bg-surface p-3';
const OUTLINE_BUTTON = 'border-line text-ink-2 hover:bg-surface-hover';

const when = (value) => formatDateTime(value) || '';

export default function HistoryDialog({ open, onOpenChange, title, pageType, entityId, entityType = '' }) {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState('');

  const fetchHistory = useCallback(async () => {
    if (!pageType || !entityId) return;
    setLoading(true);
    try {
      const res = await api.get('/undo/page-history', {
        params: { page_type: pageType, entity_id: entityId, entity_type: entityType || '' }
      });
      setItems(res.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load page history');
    } finally {
      setLoading(false);
    }
  }, [entityId, entityType, pageType]);

  const fetchDetails = async (entryId) => {
    if (!entryId) return;
    setDetailsLoading(true);
    try {
      const res = await api.get(`/undo/history/${entryId}`);
      setSelectedItem(res.data?.item || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load history details');
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setSelectedItem(null);
      return;
    }
    fetchHistory();
  }, [fetchHistory, open]);

  const handleRestore = async (entryId) => {
    const ok = await confirm({
      title: 'Restore this history point?',
      description: 'The page goes back to the saved snapshot; changes made after it are overwritten.',
      confirmLabel: 'Restore',
    });
    if (!ok) return;
    setActionLoadingId(entryId);
    try {
      await api.post(`/undo/history/${entryId}/restore`);
      toast.success('History entry restored');
      await fetchHistory();
      if (selectedItem?.id === entryId) {
        await fetchDetails(entryId);
      }
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore history entry');
    } finally {
      setActionLoadingId('');
    }
  };

  const handleRestoreStep = async (entryId, stepId) => {
    const ok = await confirm({
      title: 'Undo only this step?',
      description: 'Only this step is put back; the rest of the entry stays as it is.',
      confirmLabel: 'Undo step',
    });
    if (!ok) return;
    const loadingKey = `${entryId}:${stepId}`;
    setActionLoadingId(loadingKey);
    try {
      const res = await api.post(`/undo/history/${entryId}/steps/${stepId}/restore`);
      toast.success('History step restored');
      setSelectedItem(res.data?.item || selectedItem);
      await fetchHistory();
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore history step');
    } finally {
      setActionLoadingId('');
    }
  };

  const handleCancel = async (entryId) => {
    const ok = await confirm({
      title: 'Cancel this history entry?',
      description: 'The data stays as it is; this entry can no longer be restored.',
      confirmLabel: 'Cancel entry',
      cancelLabel: 'Keep it',
    });
    if (!ok) return;
    setActionLoadingId(entryId);
    try {
      await api.post(`/undo/history/${entryId}/cancel`);
      toast.success('History entry canceled');
      await fetchHistory();
      if (selectedItem?.id === entryId) {
        await fetchDetails(entryId);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel history entry');
    } finally {
      setActionLoadingId('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl border-line bg-surface text-ink" data-testid="history-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-ink">{title}</DialogTitle>
          <DialogDescription className="sr-only">
            Changes recorded for {typeof title === 'string' && title ? title : 'this page'}: open one for its details, restore it or undo one step.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
          <div className={CARD}>
            {/* a plain scroller: Radix ScrollArea lays its content out as a table that grows with long labels and clipped
                the status pills at the right edge */}
            <div className="h-[65vh] overflow-y-auto">
              <div className="space-y-3 p-4">
                {loading ? (
                  <p className="text-sm text-ink-3">Loading history…</p>
                ) : items.length ? items.map((item) => (
                  <div key={item.id} className={cn(ROW, selectedItem?.id === item.id && 'border-[color:var(--brand-soft-border)]')}>
                    {/* title gives way (truncates); the pill keeps its full width */}
                    <div className="flex min-w-0 items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-ink" title={item.label}>{item.label}</p>
                        <p className="mt-1 text-xs text-ink-3">{item.user_name || 'Unknown user'} · {when(item.created_at)}</p>
                        {item.change_summary ? <p className="mt-2 text-xs text-ink-2">{item.change_summary}</p> : null}
                      </div>
                      <StatusPill status={item.status} domain="history" size="sm" className="shrink-0" />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button type="button" size="sm" variant="outline" onClick={() => fetchDetails(item.id)} className={OUTLINE_BUTTON}>
                        <Eye className="mr-1 h-4 w-4" aria-hidden="true" /> Details
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleRestore(item.id)}
                        disabled={item.status !== 'available' || actionLoadingId === item.id}
                        className={OUTLINE_BUTTON}
                      >
                        <RotateCcw className="mr-1 h-4 w-4" aria-hidden="true" /> Restore
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => handleCancel(item.id)}
                        disabled={item.status !== 'available' || actionLoadingId === item.id}
                        className={OUTLINE_BUTTON}
                      >
                        <Ban className="mr-1 h-4 w-4" aria-hidden="true" /> Cancel
                      </Button>
                    </div>
                  </div>
                )) : (
                  <p className="text-sm text-ink-3">No history entries found for this page.</p>
                )}
              </div>
            </div>
          </div>

          <div className={cn(CARD, 'p-4')}>
            {detailsLoading ? (
              <p className="text-sm text-ink-3">Loading details…</p>
            ) : selectedItem ? (
              <div className="space-y-4">
                <div>
                  <p className="text-base font-semibold text-ink">{selectedItem.label}</p>
                  <p className="mt-1 text-xs text-ink-3">{selectedItem.user_name || 'Unknown user'} · {when(selectedItem.created_at)}</p>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <StatusPill status={selectedItem.status} domain="history" size="sm" />
                  {[selectedItem.page_type, selectedItem.action_type, selectedItem.entity_label].filter(Boolean).map((tag) => (
                    <span key={tag} className="rounded-md border border-line bg-surface px-2 py-0.5 text-[11.5px] font-semibold text-ink-2">{tag}</span>
                  ))}
                </div>
                <div className={ROW}>
                  <p className="ui-kpi-label">Summary</p>
                  <p className="mt-2 text-sm text-ink-2">{selectedItem.change_summary}</p>
                  {selectedItem.blocked_reason ? (
                    <p className="mt-2 text-xs font-semibold text-status-red">{selectedItem.blocked_reason}</p>
                  ) : null}
                </div>
                <div className="h-[40vh] overflow-y-auto pr-2">
                  <div className="space-y-3">
                    {selectedItem.steps?.length ? (
                      <div className={ROW}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-ink">Steps</p>
                          <p className="text-xs text-ink-3">Undo one step without restoring the whole entry</p>
                        </div>
                        <div className="mt-3 space-y-2">
                          {selectedItem.steps.map((step) => (
                            <div key={step.id} className="flex items-start justify-between gap-3 rounded-lg border border-line-2 bg-surface-2 p-2">
                              <div className="min-w-0">
                                <p className="text-xs font-semibold text-ink">{step.label}</p>
                                <p className="mt-1 text-[11px] text-ink-3">{step.collection} · {step.record_label}</p>
                                {step.blocked_reason ? <p className="mt-1 text-[11px] font-semibold text-status-red">{step.blocked_reason}</p> : null}
                              </div>
                              <div className="flex shrink-0 items-center gap-2">
                                <StatusPill status={step.status} domain="history" size="sm" />
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRestoreStep(selectedItem.id, step.id)}
                                  disabled={!step.restorable || actionLoadingId === `${selectedItem.id}:${step.id}`}
                                  className={cn('h-7 px-2 text-xs', OUTLINE_BUTTON)}
                                >
                                  Undo step
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {(selectedItem.scope_details || []).map((scope) => (
                      <div key={`${selectedItem.id}-${scope.collection}`} className={ROW}>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-ink">{scope.collection}</p>
                          <p className="text-xs text-ink-3">{scope.summary}</p>
                        </div>
                        <div className="mt-3 space-y-2">
                          {(scope.details || []).map((detail, index) => (
                            <div key={`${scope.collection}-${detail.record_key}-${index}`} className="rounded-lg border border-line-2 bg-surface-2 p-2">
                              <div className="flex min-w-0 items-center justify-between gap-2">
                                <p className="min-w-0 truncate text-xs font-semibold text-ink">{detail.record_label}</p>
                                {detail.kind ? <StatusPill status={detail.kind} size="sm" className="shrink-0" /> : null}
                              </div>
                              {detail.changed_fields?.length ? (
                                <div className="mt-2 space-y-1">
                                  {detail.changed_fields.slice(0, 12).map((field, fieldIndex) => (
                                    <p key={`${scope.collection}-${detail.record_key}-${fieldIndex}`} className="text-xs text-ink-3">
                                      {field}
                                    </p>
                                  ))}
                                </div>
                              ) : null}
                            </div>
                          ))}
                          {!scope.details?.length ? <p className="text-xs text-ink-3">No detailed changes captured.</p> : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-ink-3">Select a history entry to view details.</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
